import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import noop from 'lib/function/noop'
import type {
  OfferPageAssistantSource,
  OfferPageSnapshotContribution,
  OfferPageSnapshotInput,
} from './offerPageSnapshotTypes'

interface OfferPageAssistantSurfaceRegistryValue {
  getCurrentSnapshotInput: () => OfferPageSnapshotInput | undefined
  registerContribution: (contribution: OfferPageSnapshotContribution) => () => void
  registerSource: (source: OfferPageAssistantSource) => () => void
}

const OfferPageAssistantSurfaceRegistryContext =
  createContext<OfferPageAssistantSurfaceRegistryValue | undefined>(undefined)

interface Props {
  children: ReactNode
}

function sourceIdentity(source: OfferPageAssistantSource): string {
  return `${source.variant}:${source.key}`
}

function contributionIdentity(contribution: OfferPageSnapshotContribution): string {
  return `${contribution.sourcePath}:${contribution.key}`
}

export function useOfferPageAssistantSurfaceRegistry() {
  const sourcesRef = useRef(new Map<string, OfferPageAssistantSource>())
  const contributionsRef = useRef(new Map<string, OfferPageSnapshotContribution>())

  const registerSource = useCallback((source: OfferPageAssistantSource) => {
    const identity = sourceIdentity(source)
    if (sourcesRef.current.get(identity) === source) {
      return noop
    }

    sourcesRef.current.set(identity, source)

    return () => {
      if (sourcesRef.current.get(identity) !== source) {
        return
      }

      sourcesRef.current.delete(identity)
    }
  }, [])

  const registerContribution = useCallback((contribution: OfferPageSnapshotContribution) => {
    const identity = contributionIdentity(contribution)
    if (contributionsRef.current.get(identity) === contribution) {
      return noop
    }

    contributionsRef.current.set(identity, contribution)

    return () => {
      if (contributionsRef.current.get(identity) !== contribution) {
        return
      }

      contributionsRef.current.delete(identity)
    }
  }, [])

  const getCurrentSnapshotInput = useCallback((): OfferPageSnapshotInput | undefined => {
    const sources = [...sourcesRef.current.values()]
    if (sources.length !== 1) {
      return undefined
    }

    const contributions = [...contributionsRef.current.values()]
      .sort((left, right) => left.sourcePath.localeCompare(right.sourcePath) || left.key.localeCompare(right.key))

    return {
      source: sources[0],
      contributions,
    }
  }, [])

  return useMemo(() => ({
    getCurrentSnapshotInput,
    registerContribution,
    registerSource,
  }), [getCurrentSnapshotInput, registerContribution, registerSource])
}

export function OfferPageAssistantSurfaceProvider({ children }: Props) {
  const registry = useOfferPageAssistantSurfaceRegistry()

  return (
    <OfferPageAssistantSurfaceRegistryContext.Provider value={registry}>
      {children}
    </OfferPageAssistantSurfaceRegistryContext.Provider>
  )
}

export function useOfferPageAssistantSurfaceRegistryContext(): OfferPageAssistantSurfaceRegistryValue {
  const context = useContext(OfferPageAssistantSurfaceRegistryContext)
  if (!context) {
    throw new Error('useOfferPageAssistantSurfaceRegistryContext must be used within OfferPageAssistantSurfaceProvider')
  }

  return context
}

export function useOptionalOfferPageAssistantSurfaceRegistryContext(): OfferPageAssistantSurfaceRegistryValue | undefined {
  return useContext(OfferPageAssistantSurfaceRegistryContext)
}

interface OfferPageAssistantSourceRegistrationProps {
  source: OfferPageAssistantSource
}

export function OfferPageAssistantSourceRegistration({ source }: OfferPageAssistantSourceRegistrationProps) {
  const registry = useOptionalOfferPageAssistantSurfaceRegistryContext()

  useIsomorphicLayoutEffect(() => registry?.registerSource(source), [registry, source])

  return null
}

interface OfferPageSnapshotContributionRegistrationProps {
  contribution: OfferPageSnapshotContribution
}

export function OfferPageSnapshotContributionRegistration({
  contribution,
}: OfferPageSnapshotContributionRegistrationProps) {
  const registry = useOptionalOfferPageAssistantSurfaceRegistryContext()

  useIsomorphicLayoutEffect(() => registry?.registerContribution(contribution), [contribution, registry])

  return null
}
